import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
const STORAGE_KEY = "autoval.theme";
const ThemeContext = createContext(null);
function readInitialTheme() {
    if (typeof window === "undefined")
        return "dark";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark")
        return stored;
    // Follow the OS preference if no explicit choice has been made.
    if (window.matchMedia?.("(prefers-color-scheme: light)").matches) {
        return "light";
    }
    return "dark";
}
function applyTheme(theme) {
    if (typeof document === "undefined")
        return;
    document.documentElement.setAttribute("data-theme", theme);
}
export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(readInitialTheme);
    // Apply on mount + every change.
    useEffect(() => { applyTheme(theme); }, [theme]);
    // Track OS preference changes for users who haven't pinned a choice.
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        if (window.localStorage.getItem(STORAGE_KEY))
            return;
        const mq = window.matchMedia("(prefers-color-scheme: light)");
        const handler = (e) => {
            setThemeState(e.matches ? "light" : "dark");
        };
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    const setTheme = useCallback((next) => {
        setThemeState(next);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(STORAGE_KEY, next);
        }
    }, []);
    const toggle = useCallback(() => {
        setTheme(theme === "dark" ? "light" : "dark");
    }, [theme, setTheme]);
    const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error("useTheme must be used inside <ThemeProvider>");
    }
    return ctx;
}
/** Small icon-button toggle. Click flips dark↔light. */
export function ThemeToggle({ className = "" }) {
    const { theme, toggle } = useTheme();
    return (_jsxs("button", { type: "button", onClick: toggle, className: "inline-flex h-8 items-center gap-1 rounded-md border border-ink-600 px-2 text-xs text-ink-200 hover:bg-ink-700 " +
            className, "aria-label": `Switch to ${theme === "dark" ? "light" : "dark"} theme`, title: `Switch to ${theme === "dark" ? "light" : "dark"} theme`, children: [_jsx("span", { "aria-hidden": true, children: theme === "dark" ? "☼" : "☾" }), _jsx("span", { children: theme === "dark" ? "Light" : "Dark" })] }));
}
